import { abyssColors } from '@/theme/colors';
import { ArrowDropDown, Cancel, InfoOutlined } from '@mui/icons-material';
import { PillColorMap } from './types';

export const paddingConfig = {
  small: '0.1rem 0.55rem',
  medium: '0.3rem 0.95rem',
  large: '0.5rem 1.15rem',
};

export const iconSizeConfig = {
  small: '1.2rem',
  medium: '1.4rem',
  large: '1.6rem',
};

export const DEFAULT_ICON = <InfoOutlined />;
export const DEFAULT_CLOSE_ICON = <Cancel />;
export const DEFAULT_DROPDOWN_ICON = <ArrowDropDown />;

export const pillColorMap: PillColorMap = {
  light: {
    read: {
      success: {
        color: abyssColors.success[700],
        borderColor: abyssColors.success[50],
        backgroundColor: abyssColors.success[50],
      },
      failed: {
        color: abyssColors.error[600],
        borderColor: abyssColors.error[50],
        backgroundColor: abyssColors.error[50],
      },
      degraded: {
        color: '#D65C14',
        backgroundColor: '#F370210D',
        borderColor: '#F5B089',
      },
      warning: {
        color: abyssColors.warning[600],
        borderColor: abyssColors.warning[50],
        backgroundColor: abyssColors.warning[50],
      },
      info: {
        color: abyssColors.primary[600],
        borderColor: abyssColors.primary[100],
        backgroundColor: abyssColors.primary[100],
      },
      ready: {
        color: abyssColors.secondary[700],
        borderColor: abyssColors.secondary[50],
        backgroundColor: abyssColors.secondary[50],
      },
      linked: {
        color: abyssColors.tertiary[700],
        borderColor: abyssColors.tertiary[50],
        backgroundColor: abyssColors.tertiary[50],
      },
      disabled: {
        color: abyssColors.neutral[800],
        borderColor: abyssColors.neutral[200],
        backgroundColor: abyssColors.neutral[200],
      },
    },
    default: {
      success: {
        color: abyssColors.success[700],
        borderColor: abyssColors.success[300],
        backgroundColor: abyssColors.success[50],
      },
      failed: {
        color: abyssColors.error[600],
        borderColor: abyssColors.error[200],
        backgroundColor: abyssColors.error[50],
      },
      degraded: {
        color: '#F37021',
        borderColor: '#FFC29E',
        backgroundColor: '#F370211A',
      },
      warning: {
        color: abyssColors.warning[600],
        borderColor: abyssColors.warning[300],
        backgroundColor: abyssColors.warning[50],
      },
      info: {
        color: abyssColors.primary[600],
        borderColor: abyssColors.primary[200],
        backgroundColor: abyssColors.primary[100],
      },
      ready: {
        color: abyssColors.secondary[700],
        borderColor: abyssColors.secondary[300],
        backgroundColor: abyssColors.secondary[50],
      },
      linked: {
        color: abyssColors.tertiary[700],
        borderColor: abyssColors.tertiary[300],
        backgroundColor: abyssColors.tertiary[50],
      },
      disabled: {
        color: abyssColors.neutral[800],
        borderColor: abyssColors.neutral[400],
        backgroundColor: abyssColors.neutral[200],
      },
    },
    hover: {
      success: {
        color: abyssColors.success[800],
        borderColor: abyssColors.success[400],
        backgroundColor: abyssColors.success[200],
      },
      failed: {
        color: abyssColors.error[700],
        borderColor: abyssColors.error[500],
        backgroundColor: abyssColors.error[100],
      },
      degraded: {
        color: '#F37021',
        backgroundColor: '#F3702133',
        borderColor: '#FFB683',
      },
      warning: {
        color: abyssColors.warning[700],
        borderColor: abyssColors.warning[400],
        backgroundColor: abyssColors.warning[100],
      },
      info: {
        color: abyssColors.primary[800],
        borderColor: abyssColors.primary[500],
        backgroundColor: abyssColors.primary[200],
      },
      ready: {
        color: abyssColors.secondary[800],
        borderColor: abyssColors.secondary[600],
        backgroundColor: abyssColors.secondary[200],
      },
      linked: {
        color: abyssColors.tertiary[800],
        borderColor: abyssColors.tertiary[600],
        backgroundColor: abyssColors.tertiary[200],
      },
      disabled: {
        color: abyssColors.neutral[900],
        borderColor: abyssColors.neutral[800],
        backgroundColor: abyssColors.neutral[400],
      },
    },
    pressed: {
      success: {
        color: abyssColors.success[300],
        borderColor: abyssColors.success[200],
        backgroundColor: abyssColors.success[50],
      },
      failed: {
        color: abyssColors.error[300],
        borderColor: abyssColors.error[50],
        backgroundColor: abyssColors.error[50],
      },
      degraded: {
        color: '#C44D0E',
        backgroundColor: '#F370214D',
        borderColor: '#E39A6D',
      },
      warning: {
        color: abyssColors.warning[400],
        borderColor: abyssColors.warning[200],
        backgroundColor: abyssColors.warning[50],
      },
      info: {
        color: abyssColors.primary[300],
        borderColor: abyssColors.primary[200],
        backgroundColor: abyssColors.primary[50],
      },
      ready: {
        color: abyssColors.secondary[300],
        borderColor: abyssColors.secondary[200],
        backgroundColor: abyssColors.secondary[50],
      },
      linked: {
        color: abyssColors.tertiary[300],
        borderColor: abyssColors.tertiary[200],
        backgroundColor: abyssColors.tertiary[50],
      },
      disabled: {
        color: abyssColors.neutral[400],
        backgroundColor: abyssColors.neutral[300],
        borderColor: abyssColors.neutral[100],
      },
    },
  },
  dark: {
    read: {
      success: {
        color: abyssColors.success[300],
        borderColor: abyssColors.success[900],
        backgroundColor: abyssColors.success[900],
      },
      failed: {
        color: abyssColors.error[300],
        borderColor: abyssColors.error[900],
        backgroundColor: abyssColors.error[900],
      },
      degraded: {
        color: '#D77834',
        backgroundColor: '#2D170A',
        borderColor: '#E49769',
      },
      warning: {
        color: abyssColors.warning[300],
        borderColor: abyssColors.warning[900],
        backgroundColor: abyssColors.warning[900],
      },
      info: {
        color: abyssColors.primary[100],
        borderColor: abyssColors.primary[800],
        backgroundColor: abyssColors.primary[800],
      },
      ready: {
        color: abyssColors.secondary[200],
        borderColor: abyssColors.secondary[900],
        backgroundColor: abyssColors.secondary[900],
      },
      linked: {
        color: abyssColors.tertiary[200],
        borderColor: abyssColors.tertiary[900],
        backgroundColor: abyssColors.tertiary[900],
      },
      disabled: {
        color: abyssColors.neutral[200],
        borderColor: abyssColors.neutral[700],
        backgroundColor: abyssColors.neutral[700],
      },
    },
    default: {
      success: {
        color: abyssColors.success[300],
        borderColor: abyssColors.success[700],
        backgroundColor: abyssColors.success[700],
      },
      failed: {
        color: abyssColors.error[300],
        borderColor: abyssColors.error[700],
        backgroundColor: abyssColors.error[900],
      },
      degraded: {
        color: '#FF9241',
        backgroundColor: '#471F0C',
        borderColor: '#FFB27C',
      },
      warning: {
        color: abyssColors.warning[300],
        borderColor: abyssColors.warning[700],
        backgroundColor: abyssColors.warning[900],
      },
      info: {
        color: abyssColors.primary[100],
        borderColor: abyssColors.primary[500],
        backgroundColor: abyssColors.primary[800],
      },
      ready: {
        color: abyssColors.secondary[200],
        borderColor: abyssColors.secondary[700],
        backgroundColor: abyssColors.secondary[900],
      },
      linked: {
        color: abyssColors.tertiary[200],
        borderColor: abyssColors.tertiary[700],
        backgroundColor: abyssColors.tertiary[900],
      },
      disabled: {
        color: abyssColors.neutral[200],
        borderColor: abyssColors.neutral[600],
        backgroundColor: abyssColors.neutral[700],
      },
    },
    hover: {
      success: {
        color: abyssColors.success[200],
        borderColor: abyssColors.success[200],
        backgroundColor: abyssColors.success[700],
      },
      failed: {
        color: abyssColors.error[200],
        borderColor: abyssColors.error[200],
        backgroundColor: abyssColors.error[700],
      },
      degraded: {
        color: '#FF9241',
        backgroundColor: '#5C260E',
        borderColor: '#FFA864',
      },
      warning: {
        color: abyssColors.warning[200],
        borderColor: abyssColors.warning[200],
        backgroundColor: abyssColors.warning[700],
      },
      info: {
        color: abyssColors.primary[100],
        borderColor: abyssColors.primary[100],
        backgroundColor: abyssColors.primary[500],
      },
      ready: {
        color: abyssColors.secondary[100],
        borderColor: abyssColors.secondary[100],
        backgroundColor: abyssColors.secondary[800],
      },
      linked: {
        color: abyssColors.tertiary[50],
        borderColor: abyssColors.tertiary[50],
        backgroundColor: abyssColors.tertiary[700],
      },
      disabled: {
        color: abyssColors.neutral[200],
        borderColor: abyssColors.neutral[200],
        backgroundColor: abyssColors.neutral[600],
      },
    },
    pressed: {
      success: {
        color: abyssColors.success[600],
        borderColor: abyssColors.success[600],
        backgroundColor: abyssColors.success[900],
      },
      failed: {
        color: abyssColors.error[700],
        borderColor: abyssColors.error[700],
        backgroundColor: abyssColors.error[900],
      },
      degraded: {
        color: '#C45F18',
        backgroundColor: '#351B0C',
        borderColor: '#CC814A',
      },
      warning: {
        color: abyssColors.warning[600],
        borderColor: abyssColors.warning[600],
        backgroundColor: abyssColors.warning[900],
      },
      info: {
        color: abyssColors.primary[400],
        borderColor: abyssColors.primary[400],
        backgroundColor: abyssColors.primary[900],
      },
      ready: {
        color: abyssColors.secondary[700],
        borderColor: abyssColors.secondary[700],
        backgroundColor: abyssColors.secondary[900],
      },
      linked: {
        color: abyssColors.tertiary[700],
        borderColor: abyssColors.tertiary[700],
        backgroundColor: abyssColors.tertiary[900],
      },
      disabled: {
        color: abyssColors.neutral[700],
        borderColor: abyssColors.neutral[700],
        backgroundColor: abyssColors.neutral[900],
      },
    },
  },
};
